import {
  ChangeEvent,
  HTMLAttributes,
  forwardRef,
  useCallback,
  KeyboardEventHandler,
} from 'react';
import { Icon } from '~/components/atomic';
import { IconProps } from '~/components/atomic/Icon/Icon';
import { InputLabelTitle } from '~/components/common';
import * as S from './Input.styled';

export interface InputProps extends HTMLAttributes<HTMLInputElement> {
  type?: string;
  id: string;
  label?: string;
  text: string;
  setText: (text: string) => void;
  placeholder: string;
  deleteAllTextOpt?: boolean;
  textLengthOpt?: boolean;
  maxText?: number;
  leftIconName?: IconProps['iconName'];
  isFullWidth?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  inputWrapperName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      id,
      label,
      placeholder,
      text,
      setText,
      deleteAllTextOpt,
      textLengthOpt,
      maxText = 30,
      leftIconName,
      isFullWidth,
      onChange,
      onKeyDown,
      inputWrapperName,
      ...props
    },
    forwardedRef,
  ) => {
    const handleDeleteText = (e: React.MouseEvent) => {
      e.stopPropagation();
      setText('');
    };

    const handleTextChange = useCallback(
      (setText: (text: string) => void) => {
        return (e: React.ChangeEvent) => {
          const inputText = (e.target as HTMLInputElement).value;
          if (maxText !== undefined && inputText.length > maxText) {
            setText(inputText.substring(0, maxText));
          } else {
            setText(inputText);
          }
        };
      },
      [maxText],
    );

    return (
      <S.Container isFullWidth={isFullWidth}>
        {label && <InputLabelTitle title={label} id={id} />}
        <S.InputBox
          paddingWide={deleteAllTextOpt}
          leftIconName={leftIconName}
          className={inputWrapperName}
        >
          {leftIconName && <Icon iconName={leftIconName} />}
          <input
            type={type}
            ref={forwardedRef}
            id={id}
            placeholder={placeholder}
            value={text}
            onChange={onChange ? onChange : handleTextChange(setText)}
            onKeyDown={onKeyDown}
            {...props}
          />
          {deleteAllTextOpt && text !== '' && (
            <S.DeleteButton onClick={handleDeleteText}>
              <Icon color='hierarchy1' size={20} iconName='Close' />
            </S.DeleteButton>
          )}
        </S.InputBox>
        {textLengthOpt && (
          <S.TextValueBox>
            <S.TextValue>
              {text.length} / {maxText}
            </S.TextValue>
          </S.TextValueBox>
        )}
      </S.Container>
    );
  },
);

export default Input;
